import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, Observable, Subscription} from "rxjs";
import {FormGroup} from "@angular/forms";
import {OrderState} from "../models/subscription-order.model";
import {SubscriptionOrderConverter} from "../../core/utils/subscription-order-converter";

@Injectable({
  providedIn: 'root'
})
export class OrderProcessingService {
  private readonly subscriptionOrderConverter = inject(SubscriptionOrderConverter);

  /* Wenn der Service initialisiert wird, bedeutet es, dass ein Abo gerade angelegt wird.
  Dieses Objekt wird dann Schritt für Schritt befüllt und am Ende an das Backend weitergeleitet */

  private orderStateNew = new BehaviorSubject<OrderState>(this.createInitialState());

  getOrderState(): Observable<OrderState> {
    return this.orderStateNew;
  }

  update(mutator: (state: OrderState) => OrderState) {
    this.orderStateNew.next(mutator(this.orderStateNew.value));
  }

  resetState(): void {
    this.orderStateNew.next(this.createInitialState());
  }

  public buildOrderDto(): any {
    return this.subscriptionOrderConverter.convert(this.orderStateNew.value);
  }

  snapshot(): OrderState;
  snapshot<T>(selector: (state: any) => T): T;
  snapshot<T>(selector?: (state: any) => T): T | any {
    const state = this.orderStateNew.value;
    return selector ? selector(state) : state;
  }

  /**
   * Gibt die Möglichkeit, die Werte aus einer Form direkt an das Objekt zu schreiben, welches von dem Service
   * verwaltet wird.
   *
   * `options.rawValue` übernimmt statt des emittierten Formularwerts den Rohwert (`getRawValue()`), der auch
   * deaktivierte Controls enthält. Nötig für Formulare, deren Felder nur zur Anzeige deaktiviert werden,
   * inhaltlich aber zur Bestellung gehören (readonly-Lieferadresse): Angular lässt deaktivierte Kinder aus
   * `FormGroup.value` fallen, ein Emit während einer disable()-Kaskade liefert deshalb einen Teilwert, der
   * die bereits geladenen Daten im OrderState überschreibt.
   *
   * Standard ist `false`, denn Formulare mit zuklappbaren Blöcken (abweichende Rechnungsadresse,
   * Bonus-/Werber-Adresse) nutzen `disable()` bewusst als Übertragungsfilter und brauchen genau das
   * Weglassen deaktivierter Controls.
   *
   * Ein Block kann beides sein - zugeklappt Filter, aufgeklappt readonly-Anzeige. Für ihn nimmt
   * `rawValue` ein Prädikat, das pro Änderung entscheidet (Rechnungsadresse bei ONLY_BILLING).
   */
  connectForm<T>(
    form: FormGroup,
    path: (state: OrderState) => OrderState,
    updater: (state: OrderState, value: T) => OrderState,
    options: { rawValue?: boolean | (() => boolean) } = {}
  ): Subscription {
    return form.valueChanges.subscribe(value => {
      const useRawValue = typeof options.rawValue === 'function' ? options.rawValue() : !!options.rawValue;
      const formValue: T = useRawValue ? form.getRawValue() as T : value as T;
      this.update(state => updater(state, formValue));
    });
  }

  private createInitialState(): OrderState {
    return {
      additionalData: [],
      addresses: {billingAddress: undefined, deliveryAddress: undefined, email: ""},
      billingAddressAskedInAddressStep: false,
      bonusData: undefined,
      clientNoUrl: "",
      offer: undefined,
      offerPrice: [],
      calculatingPrice: false,
      onlinePaymentFinished: false,
      optinList: undefined,
      payment: undefined,
      phoneNumber: {phone: "", phoneCode: ""},
      redirectUrl: "",
      registration: undefined,
      transaction: undefined,
      urlParams: undefined,
      userAgreement: undefined,
      userBackendId: undefined,
      userData: undefined,
      userSSOId: 0,
      validDate: {validFrom: undefined, validUntil: undefined}
    };
  }
}
